import React, { useCallback, useEffect, useRef, useState } from "react";
import { CROP_JPEG_QUALITY } from "./constants";

interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

interface Props {
  dataUrl: string;
  onCrop: (croppedDataUrl: string) => void;
}

const HANDLE_SIZE = 10;
const HANDLE_HIT_SIZE = 30;
const MIN_SIZE = 20;

type DragMode =
  | { type: "move"; startX: number; startY: number; origRect: Rect }
  | {
      type: "resize";
      corner: "nw" | "ne" | "se" | "sw";
      startX: number;
      startY: number;
      origRect: Rect;
    }
  | null;

export function ImageCropper({ dataUrl, onCrop }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // Crop rect as fractions [0-1] relative to the displayed image
  const [rect, setRect] = useState<Rect>({ x: 0.05, y: 0.05, w: 0.9, h: 0.9 });
  const dragRef = useRef<DragMode>(null);

  // Load image once to get natural dimensions for the final crop
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
    };
    img.src = dataUrl;
  }, [dataUrl]);

  const clamp = (v: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, v));

  const getRelPos = (e: React.PointerEvent | PointerEvent) => {
    const el = containerRef.current;
    if (!el) return { rx: 0, ry: 0 };
    const { left, top, width, height } = el.getBoundingClientRect();
    return {
      rx: (e.clientX - left) / width,
      ry: (e.clientY - top) / height,
    };
  };

  const hitTest = (rx: number, ry: number): DragMode => {
    const { x, y, w, h } = rect;
    const hs =
      HANDLE_HIT_SIZE /
      (containerRef.current?.getBoundingClientRect().width ?? 300);
    const corners: Array<["nw" | "ne" | "se" | "sw", number, number]> = [
      ["nw", x, y],
      ["ne", x + w, y],
      ["se", x + w, y + h],
      ["sw", x, y + h],
    ];
    for (const [corner, cx, cy] of corners) {
      if (Math.abs(rx - cx) < hs && Math.abs(ry - cy) < hs) {
        return {
          type: "resize",
          corner,
          startX: rx,
          startY: ry,
          origRect: { ...rect },
        };
      }
    }
    if (rx >= x && rx <= x + w && ry >= y && ry <= y + h) {
      return { type: "move", startX: rx, startY: ry, origRect: { ...rect } };
    }
    return null;
  };

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const { rx, ry } = getRelPos(e);
      const hit = hitTest(rx, ry);
      if (!hit) return;
      dragRef.current = hit;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      e.preventDefault();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [rect],
  );

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    const drag = dragRef.current;
    if (!drag) return;
    const { rx, ry } = getRelPos(e);
    const dx = rx - drag.startX;
    const dy = ry - drag.startY;
    const o = drag.origRect;

    if (drag.type === "move") {
      setRect({
        x: clamp(o.x + dx, 0, 1 - o.w),
        y: clamp(o.y + dy, 0, 1 - o.h),
        w: o.w,
        h: o.h,
      });
    } else {
      let { x, y, w, h } = o;
      switch (drag.corner) {
        case "nw":
          x = clamp(o.x + dx, 0, o.x + o.w - MIN_SIZE / 300);
          y = clamp(o.y + dy, 0, o.y + o.h - MIN_SIZE / 300);
          w = o.x + o.w - x;
          h = o.y + o.h - y;
          break;
        case "ne":
          y = clamp(o.y + dy, 0, o.y + o.h - MIN_SIZE / 300);
          w = clamp(o.w + dx, MIN_SIZE / 300, 1 - o.x);
          h = o.y + o.h - y;
          break;
        case "se":
          w = clamp(o.w + dx, MIN_SIZE / 300, 1 - o.x);
          h = clamp(o.h + dy, MIN_SIZE / 300, 1 - o.y);
          break;
        case "sw":
          x = clamp(o.x + dx, 0, o.x + o.w - MIN_SIZE / 300);
          w = o.x + o.w - x;
          h = clamp(o.h + dy, MIN_SIZE / 300, 1 - o.y);
          break;
      }
      setRect({ x, y, w, h });
    }
  }, []);

  const onPointerUp = useCallback(() => {
    dragRef.current = null;
  }, []);

  const getCursor = (rx: number, ry: number) => {
    const drag = dragRef.current;
    if (drag?.type === "move") return "grabbing";
    const { x, y, w, h } = rect;
    const hs =
      HANDLE_HIT_SIZE /
      (containerRef.current?.getBoundingClientRect().width ?? 300);
    if (Math.abs(rx - x) < hs && Math.abs(ry - y) < hs) return "nw-resize";
    if (Math.abs(rx - (x + w)) < hs && Math.abs(ry - y) < hs)
      return "ne-resize";
    if (Math.abs(rx - (x + w)) < hs && Math.abs(ry - (y + h)) < hs)
      return "se-resize";
    if (Math.abs(rx - x) < hs && Math.abs(ry - (y + h)) < hs)
      return "sw-resize";
    if (rx >= x && rx <= x + w && ry >= y && ry <= y + h) return "grab";
    return "crosshair";
  };

  const [cursor, setCursor] = useState("crosshair");
  const onMouseMove = useCallback(
    (e: React.PointerEvent) => {
      onPointerMove(e);
      const { rx, ry } = getRelPos(e);
      setCursor(getCursor(rx, ry));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [rect, onPointerMove],
  );

  const handleCrop = () => {
    const img = imgRef.current;
    if (!img) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const cropW = rect.w * nw;
    const cropH = rect.h * nh;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(cropW));
    canvas.height = Math.max(1, Math.round(cropH));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(
      img,
      rect.x * nw,
      rect.y * nh,
      cropW,
      cropH,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    onCrop(canvas.toDataURL("image/jpeg", CROP_JPEG_QUALITY));
  };

  const pct = (v: number) => `${(v * 100).toFixed(2)}%`;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {/* Canvas area */}
      <div
        ref={containerRef}
        style={{
          position: "relative",
          userSelect: "none",
          touchAction: "none",
          borderRadius: 10,
          overflow: "hidden",
          background: "#000",
          cursor,
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onMouseMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <img
          src={dataUrl}
          alt="Crop"
          style={{ width: "100%", display: "block" }}
          draggable={false}
        />

        {/* Dark overlay outside selection */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {/* top */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: pct(rect.y),
              background: "rgba(0,0,0,0.45)",
            }}
          />
          {/* bottom */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: pct(1 - rect.y - rect.h),
              background: "rgba(0,0,0,0.45)",
            }}
          />
          {/* left */}
          <div
            style={{
              position: "absolute",
              top: pct(rect.y),
              left: 0,
              width: pct(rect.x),
              height: pct(rect.h),
              background: "rgba(0,0,0,0.45)",
            }}
          />
          {/* right */}
          <div
            style={{
              position: "absolute",
              top: pct(rect.y),
              right: 0,
              width: pct(1 - rect.x - rect.w),
              height: pct(rect.h),
              background: "rgba(0,0,0,0.45)",
            }}
          />
        </div>

        {/* Selection border + rule-of-thirds grid */}
        <div
          style={{
            position: "absolute",
            left: pct(rect.x),
            top: pct(rect.y),
            width: pct(rect.w),
            height: pct(rect.h),
            border: "1.5px solid rgba(255,255,255,0.9)",
            boxSizing: "border-box",
            pointerEvents: "none",
          }}
        >
          {/* Rule-of-thirds lines */}
          {[1, 2].map((i) => (
            <React.Fragment key={i}>
              <div
                style={{
                  position: "absolute",
                  left: `${(i / 3) * 100}%`,
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: "rgba(255,255,255,0.25)",
                }}
              />
              <div
                style={{
                  position: "absolute",
                  top: `${(i / 3) * 100}%`,
                  left: 0,
                  right: 0,
                  height: 1,
                  background: "rgba(255,255,255,0.25)",
                }}
              />
            </React.Fragment>
          ))}
        </div>

        {/* Corner handles */}
        {(["nw", "ne", "se", "sw"] as const).map((corner) => {
          const cx = corner.includes("e") ? rect.x + rect.w : rect.x;
          const cy = corner.includes("s") ? rect.y + rect.h : rect.y;
          return (
            <div
              key={corner}
              style={{
                position: "absolute",
                left: `calc(${pct(cx)} - ${HANDLE_SIZE / 2}px)`,
                top: `calc(${pct(cy)} - ${HANDLE_SIZE / 2}px)`,
                width: HANDLE_SIZE,
                height: HANDLE_SIZE,
                background: "white",
                borderRadius: 2,
                boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                pointerEvents: "none",
              }}
            />
          );
        })}
      </div>

      {/* Crop button */}
      <button
        onClick={handleCrop}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "14px 16px",
          borderRadius: 12,
          background: "#2D6A4F",
          border: "none",
          cursor: "pointer",
          fontSize: 15,
          fontWeight: 600,
          color: "white",
          transition: "background 0.15s",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "#245c43";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "#2D6A4F";
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M6 2v14a2 2 0 0 0 2 2h14"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M18 22V8a2 2 0 0 0-2-2H2"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Crop image
      </button>
    </div>
  );
}
